import React, { useContext } from "react";

interface WizardStepContextState {
  currentStepNumber: number;
  maxStepNumber: number;
  onCancel: (isCanceled: boolean) => void;
}

interface WizardStepContextProviderProps extends WizardStepContextState {
  setStepNumber: React.Dispatch<React.SetStateAction<number>>;
  children: React.ReactNode;
}

const MIN_STEP_NUMBER = 0;
const DEFAULT_ON_CANCEL_FUNCTION = (): void => {};
const DEFAULT_WIZARD_STEP_CONTEXT_STATE: WizardStepContextState = {
  currentStepNumber: 0,
  maxStepNumber: 0,
  onCancel: DEFAULT_ON_CANCEL_FUNCTION,
};
Object.freeze(DEFAULT_WIZARD_STEP_CONTEXT_STATE);

const WizardStepContext = React.createContext<WizardStepContextState>(
  DEFAULT_WIZARD_STEP_CONTEXT_STATE
);
const WizardStepDispatchContext = React.createContext<
  React.Dispatch<React.SetStateAction<number>>
>(() => {});

/**
 * Hook that wraps step context. Used to prevent misuse of context.
 *
 * @export
 * @returns {WizardStepContextProviderProps}
 */
export function useStepContext(): WizardStepContextState & {
  minStepNumber: number;
  setStepNumber: React.Dispatch<React.SetStateAction<number>>;
} {
  const { currentStepNumber, maxStepNumber, onCancel } = useContext(
    WizardStepContext
  );
  const setStepNumber = useContext(WizardStepDispatchContext);

  return {
    currentStepNumber,
    minStepNumber: MIN_STEP_NUMBER,
    maxStepNumber,
    setStepNumber,
    onCancel,
  };
}

const WizardStepContextProvider: React.FC<WizardStepContextProviderProps> = ({
  currentStepNumber,
  maxStepNumber,
  setStepNumber,
  onCancel = DEFAULT_ON_CANCEL_FUNCTION,
  children,
}): JSX.Element => {
  const wizardStepState = {
    currentStepNumber,
    maxStepNumber,
    onCancel,
  };

  return (
    <WizardStepDispatchContext.Provider value={setStepNumber}>
      <WizardStepContext.Provider value={wizardStepState}>
        {children}
      </WizardStepContext.Provider>
    </WizardStepDispatchContext.Provider>
  );
};

export default WizardStepContextProvider;
